import { supabase } from '@shared/supabase/client';

// Web Push helpers for the creator app. VAPID public key is safe to expose;
// the private key lives server-side (app_push_config, read only by the
// send-campaign-push edge fn). Push works in PROD (https + registered sw.js).
const VAPID_PUBLIC = 'BJlZ95G2AJRKJFZYHQ8qvpBkdBOc6PCJB0bKOHLhhva41Khd6vJJgl38P2AtyY0z0JddJrrRSKaUURQKYihSgDc';

export type PushState = 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed';

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return existing;
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await getRegistration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'unsubscribed';
}

export async function enablePush(providerId: string | null): Promise<{ ok: boolean; error?: string }> {
  if (!pushSupported()) return { ok: false, error: 'unsupported' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, error: 'denied' };
  const reg = await getRegistration();
  if (!reg) return { ok: false, error: 'no-sw' };
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC) as BufferSource,
    });
  }
  const j = sub.toJSON();
  const endpoint = sub.endpoint;
  const p256dh = j.keys?.p256dh;
  const auth = j.keys?.auth;
  if (!endpoint || !p256dh || !auth) return { ok: false, error: 'bad-subscription' };
  const { error } = await supabase.from('push_subscriptions').upsert(
    { endpoint, p256dh, auth, instagram_provider_id: providerId, user_agent: navigator.userAgent, last_ok_at: new Date().toISOString() },
    { onConflict: 'endpoint' },
  );
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function disablePush(): Promise<void> {
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
}
